import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import moment from 'moment/moment'
import lightTheme from '../../styles/theme/lightTheme'
import * as React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faCreditCard,
  faHourglassEnd,
  faEye,
  faBan,
  faEllipsisVertical,
} from '@fortawesome/pro-regular-svg-icons'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { faGem } from '@fortawesome/free-solid-svg-icons'
import { faHourglassStart } from '@fortawesome/pro-solid-svg-icons'
import DetailSubscription from '../modals/DetailSubscription'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
}

const TablePanelBox = styled(TabPanel)(({ theme }) => ({
  marginBottom: 10,
  backgroundColor: 'rgb(227, 230, 255, 0.5)',
  '& .MuiBox-root': {
    padding: 0,
  },
}))

const HeaderTab = styled(TableHead)(({ theme }) => ({
  height: 50,
  backgroundColor: lightTheme.palette.primary.b6,
}))

const TitleHead = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  whiteSpace: 'nowrap',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const BodyTable = styled(TableBody)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: '2px solid rgb(227, 230, 255, 0.5)',
  fontFamily: 'Century Gothic',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  verticalAlign: 'top',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const ButtonIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  border: `1px solid ${lightTheme.palette.primary.b2}`,
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  margin: '0 4px',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    border: `2px solid ${lightTheme.palette.primary.b2}`,
    backgroundColor: 'rgba(102, 126, 234, 0.06)',
  },
}))

const StyledActionMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 18,
    minWidth: 220,
    border: '1px solid rgba(99, 102, 241, 0.15)',
    boxShadow: '0 24px 60px rgba(99, 102, 241, 0.18)',
  },
}))

const StyledActionMenuItem = styled(MenuItem)(({ theme }) => ({
  px: 2.5,
  py: 1.25,
  borderRadius: 12,
  gap: 10,
  color: lightTheme.palette.text.primary,
  fontFamily: 'Century Gothic',
  fontSize: 14,
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
  },
  '& .menuActionIcon': {
    minWidth: 24,
    color: '#667eea',
  },
}))

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'statusvariant',
})(({ statusvariant }) => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  height: 24,
  borderRadius: '8px',
  ...(statusvariant === 'active' && {
    backgroundColor: 'rgba(76, 175, 80, 0.12)',
    color: '#2e7d32',
  }),
  ...(statusvariant === 'noCredit' && {
    backgroundColor: 'rgba(244, 67, 54, 0.12)',
    color: '#c62828',
  }),
  ...(statusvariant === 'pending' && {
    backgroundColor: 'rgba(255, 152, 0, 0.12)',
    color: '#e65100',
  }),
  ...(statusvariant === 'expired' && {
    backgroundColor: 'rgba(102, 126, 234, 0.12)',
    color: '#5c6bc0',
  }),
  ...(statusvariant === 'pay' && {
    backgroundColor: 'rgba(102, 126, 234, 0.12)',
    color: '#667eea',
  }),
}))

const getPlanName = (offre, language) =>
  language === 'fr'
    ? offre.subscriptionPlan?.name
    : offre.subscriptionPlan?.nameEn || offre.subscriptionPlan?.name

const getFormattedPrice = (offre) => {
  try {
    const currency = sessionStorage.getItem('currency')
      ? JSON.parse(sessionStorage.getItem('currency'))
      : { currentRate: 1, devise: 'CHF' }
    const amount = (
      (offre.finalPrice || 0) * (currency.currentRate || 1)
    ).toFixed(2)
    return `${currency.devise || 'CHF'} ${amount}`
  } catch {
    return `${'CHF'} ${(offre.finalPrice || 0).toFixed(2)}`
  }
}

const buildPaymentOffer = (offre, language) => {
  const pricingData = {
    monthly: offre.subscriptionPlan.basePrice,
    3: offre.subscriptionPlan.basePrice * 3,
    6: offre.subscriptionPlan.basePrice * 6,
    12: offre.subscriptionPlan.basePrice * 12,
  }

  if (
    offre.subscriptionPlan.pricingTiers &&
    offre.subscriptionPlan.pricingTiers.length > 0
  ) {
    try {
      const parsedPricingData = JSON.parse(
        offre.subscriptionPlan.pricingTiers[0]
      )
      if (Array.isArray(parsedPricingData)) {
        parsedPricingData.forEach((tier) => {
          if (tier.months === 3 && tier.price !== 0)
            pricingData['3'] = tier.price
          if (tier.months === 6 && tier.price !== 0)
            pricingData['6'] = tier.price
          if (tier.months === 12 && tier.price !== 0)
            pricingData['12'] = tier.price
        })
      }
    } catch (e) {
      console.log('Error parsing pricing tiers:', e)
    }
  }

  return {
    id: offre.subscriptionPlan.id,
    name: getPlanName(offre, language),
    icon: faGem,
    iconColor: '#66bb6a',
    monthlyPrice: pricingData.monthly,
    threeMonthsPrice: pricingData['3'],
    sixMonthsPrice: pricingData['6'],
    twelveMonthsPrice: pricingData['12'],
    pricingData,
    currency: 'CHF',
    period: 'mois',
    features: (offre.subscriptionPlan.features || []).map((feature) =>
      language === 'fr' ? feature.name : feature.nameEn || feature.name
    ),
    ideal:
      (language === 'fr'
        ? offre.subscriptionPlan.description
        : offre.subscriptionPlan.descriptionEn) ||
      offre.subscriptionPlan.description,
    featured:
      offre.subscriptionPlan.name === 'Boost' ||
      offre.subscriptionPlan.nameEn === 'Boost',
    quantity: 1,
  }
}

const OffreEntrepriseTable = ({ offres, handlePayementOffre }) => {
  const { t, i18n } = useTranslation()
  const [selectedOffre, setSelectedOffre] = useState(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuOffre, setMenuOffre] = useState(null)

  const navigate = useNavigate()

  const handleMenuOpen = (event, offre) => {
    setAnchorEl(event.currentTarget)
    setMenuOffre(offre)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuOffre(null)
  }

  const handlePaymentClick = (offre) => {
    const offerWithQuantity = buildPaymentOffer(offre, i18n.language)
    sessionStorage.setItem('offerSelected', JSON.stringify(offerWithQuantity))
    navigate('/entreprises/offres')
    handleMenuClose()
  }

  const getStatusConfig = (offre) => {
    if (offre.status === 'payer') {
      if (offre.credits > 0) {
        return {
          variant: 'active',
          label: t('status.active'),
          icon: faCheck,
          color: lightTheme.palette.success.main,
          canPay: false,
        }
      }
      return {
        variant: 'noCredit',
        label: t('status.noCredit'),
        icon: faBan,
        color: lightTheme.palette.error.main,
        canPay: false,
      }
    }
    if (offre.status === 'non-payer') {
      return {
        variant: 'pending',
        label: t('status.pending'),
        icon: faHourglassStart,
        color: lightTheme.palette.warning.main,
        canPay: false,
      }
    }
    if (offre.status === 'expired') {
      return {
        variant: 'expired',
        label: t('status.expired'),
        icon: faHourglassEnd,
        color: lightTheme.palette.primary.main,
        canPay: false,
      }
    }
    return {
      variant: 'pay',
      label: t('status.pending'),
      icon: faCreditCard,
      color: lightTheme.palette.primary.main,
      canPay: true,
    }
  }

  const renderStatus = (offre, size = 'small') => {
    const config = getStatusConfig(offre)
    if (config.canPay && offre.subscriptionPlan.trialOffer) {
      return <></>
    }
    if (config.canPay) {
      return (
        <Tooltip title={t('payment.renewSubscription')} arrow>
          <Box
            component="span"
            sx={{ cursor: 'pointer', display: 'inline-flex' }}
            onClick={() => handlePaymentClick(offre)}
          >
            <StatusChip
              size={size}
              statusvariant={config.variant}
              label={t('button.pay', 'Payer')}
              icon={
                <FontAwesomeIcon icon={faCreditCard} style={{ fontSize: 12 }} />
              }
            />
          </Box>
        </Tooltip>
      )
    }
    return (
      <StatusChip
        size={size}
        statusvariant={config.variant}
        label={config.label}
        icon={<FontAwesomeIcon icon={config.icon} style={{ fontSize: 12 }} />}
      />
    )
  }

  return (
    <>
      <TablePanelBox value={0} index={0}>
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table
            stickyHeader
            aria-label="offers table"
            sx={{ minWidth: { xs: 280, sm: 'auto' } }}
          >
            <HeaderTab>
              <TableRow>
                <TitleHead sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {t('profile.date')}
                </TitleHead>
                <TitleHead sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {t('profile.dateExpire')}
                </TitleHead>
                <TitleHead sx={{ minWidth: { xs: 160, sm: 'auto' } }}>
                  {t('header.offer')}
                </TitleHead>
                <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {t('profile.price')}
                </TitleHead>
                <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {t('profile.status')}
                </TitleHead>
                <TitleHead
                  sx={{
                    width: { xs: 56, sm: 140 },
                    textAlign: 'center',
                  }}
                >
                  {t('profile.actions')}
                </TitleHead>
              </TableRow>
            </HeaderTab>
            <BodyTable>
              {offres && offres.length > 0 ? (
                offres.map((offre) => (
                  <TableRow
                    key={
                      offre.id ??
                      `${offre.createdAt}-${offre.subscriptionPlan?.id}`
                    }
                  >
                    <StyledTableCell
                      sx={{ display: { xs: 'none', md: 'table-cell' } }}
                    >
                      {moment(offre.createdAt)
                        .tz('Europe/Paris')
                        .format('DD/MM/YYYY HH:mm')}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ display: { xs: 'none', md: 'table-cell' } }}
                    >
                      {moment(offre.endDate)
                        .tz('Europe/Paris')
                        .format('DD/MM/YYYY')}
                    </StyledTableCell>
                    <StyledTableCell sx={{ minWidth: { xs: 160, sm: 'auto' } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          variant="body2"
                          onClick={() => setSelectedOffre(offre)}
                          sx={{
                            color: '#667eea',
                            cursor: 'pointer',
                            fontWeight: 600,
                            lineHeight: 1.35,
                            '&:hover': {
                              color: '#5a67d8',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {getPlanName(offre, i18n.language)}
                        </Typography>

                        <Box
                          sx={{
                            display: { xs: 'flex', md: 'none' },
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600 }}
                            >
                              {t('profile.date')}:
                            </Typography>
                            <Typography variant="caption">
                              {moment(offre.createdAt)
                                .tz('Europe/Paris')
                                .format('DD/MM/YYYY')}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600 }}
                            >
                              {t('profile.dateExpire')}:
                            </Typography>
                            <Typography variant="caption">
                              {moment(offre.endDate)
                                .tz('Europe/Paris')
                                .format('DD/MM/YYYY')}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600 }}
                            >
                              {t('profile.price')}:
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600 }}
                            >
                              {getFormattedPrice(offre)}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            {renderStatus(offre, 'small')}
                          </Box>
                        </Box>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {offre.subscriptionPlan.trialOffer
                          ? t('payment.free')
                          : getFormattedPrice(offre)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                    >
                      {renderStatus(offre)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: { xs: 'flex', sm: 'none' },
                          justifyContent: 'center',
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, offre)}
                          aria-label={t('profile.actions')}
                        >
                          <FontAwesomeIcon
                            icon={faEllipsisVertical}
                            color={lightTheme.palette.primary.b2}
                            fontSize={16}
                          />
                        </IconButton>
                      </Box>

                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          justifyContent: 'right',
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        {!offre.subscriptionPlan.trialOffer &&
                          getStatusConfig(offre).canPay && (
                            <Tooltip
                              title={t('payment.renewSubscription')}
                              arrow
                            >
                              <ButtonIcon
                                onClick={() => handlePaymentClick(offre)}
                              >
                                <FontAwesomeIcon
                                  icon={faCreditCard}
                                  fontSize={16}
                                  color={lightTheme.palette.primary.b2}
                                />
                              </ButtonIcon>
                            </Tooltip>
                          )}
                        <Tooltip title={t('button.view', 'Voir')} arrow>
                          <ButtonIcon onClick={() => setSelectedOffre(offre)}>
                            <FontAwesomeIcon
                              icon={faEye}
                              fontSize={16}
                              color={lightTheme.palette.primary.b2}
                            />
                          </ButtonIcon>
                        </Tooltip>
                      </Box>

                      <StyledActionMenu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuOffre === offre}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        PaperProps={{
                          sx: {
                            p: 1,
                            bgcolor: '#fff',
                          },
                        }}
                      >
                        <StyledActionMenuItem
                          onClick={() => {
                            setSelectedOffre(offre)
                            handleMenuClose()
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faEye}
                            className="menuActionIcon"
                            fontSize={14}
                          />
                          <Typography variant="body2">
                            {t('button.view')}
                          </Typography>
                        </StyledActionMenuItem>
                        {getStatusConfig(offre).canPay && (
                          <StyledActionMenuItem
                            onClick={() => handlePaymentClick(offre)}
                          >
                            <FontAwesomeIcon
                              icon={faCreditCard}
                              className="menuActionIcon"
                              fontSize={14}
                            />
                            <Typography variant="body2">
                              {t('button.pay', 'Payer')}
                            </Typography>
                          </StyledActionMenuItem>
                        )}
                      </StyledActionMenu>
                    </StyledTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2">
                      {t('dashboard.noOffers', 'Aucune offre')}
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </BodyTable>
          </Table>
        </Box>
      </TablePanelBox>

      <DetailSubscription
        selectedOffre={selectedOffre}
        setSelectedOffre={setSelectedOffre}
      />
    </>
  )
}

export default OffreEntrepriseTable
